'use client';

import { createContext, ReactNode, useContext, useState } from "react";
import { SiteHistory } from "@/app/lib/generated/graphql-types";
import {
    useSingleTypeEditor,
    UseSingleTypeEditorType,
} from "@/app/lib/hooks/use-editor/useSingleTypeEditor";
import { SiteHistoryEditable } from "@/app/features/sites/history/types/types";
import { SiteHistorySchema } from "@/app/features/sites/history/types/schema";
import { getSiteHistory } from "@/app/features/sites/history/data-access/queries";
import { updateSiteHistory } from "@/app/features/sites/history/data-access/mutations";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import FloatingEditorMenu from "@/app/features/shared/__to_be_integrated/FloatingEditorMenu";
import { toggleDraftMode } from "@/app/lib/actions/editing";
import { Add } from "@mui/icons-material";
import AddHistoryEventDialog from "@/app/features/sites/history/ui/editable/AddHistoryEventDialog";

type SiteHistoryEditorContextType = UseSingleTypeEditorType<SiteHistoryEditable>;

const SiteHistoryEditorContext = createContext<SiteHistoryEditorContextType | undefined>(undefined);

export function useSiteHistoryEditor() {
    const context = useContext(SiteHistoryEditorContext);
    if (!context) {
        throw new Error('useSiteHistoryEditor must be used within SiteHistoryEditorProvider');
    }
    return context;
}

export default function SiteHistoryEditorProvider({
    children,
    isDraftModeEnabled,
}: {
    children: ReactNode;
    isDraftModeEnabled: boolean;
}) {
    const [isAddEventOpen, setIsAddEventOpen] = useState(false);
    const editor = useSingleTypeEditor<SiteHistory, SiteHistoryEditable, SiteHistoryEditable>({
        schema: SiteHistorySchema,
        loadEntity: getSiteHistory,
        update: updateSiteHistory,
        // updateSiteHistory updates the linked showroom before mapping the
        // history input. Keep the editable shape intact until that mutation
        // can perform both steps.
        toInput: (editable) => editable,
    });

    if (editor.isLoading) return <LoadingSpinner />;

    return (
        <SiteHistoryEditorContext.Provider value={editor}>
            {children}
            <FloatingEditorMenu
                autosaveState={editor.autosaveState || undefined}
                isDraftModeEnabled={isDraftModeEnabled}
                toggleDraftMode={toggleDraftMode}
                hasChanges={editor.hasChanges}
                onPublish={editor.publish}
                onDiscard={() => void editor.discardChanges()}
                actions={[{
                    label: "Ereignis hinzufügen",
                    startIcon: <Add />,
                    onClick: () => setIsAddEventOpen(true),
                }]}
            />
            <AddHistoryEventDialog open={isAddEventOpen} onClose={() => setIsAddEventOpen(false)} />
        </SiteHistoryEditorContext.Provider>
    );
}
